import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRecoilState } from "recoil";
import { DocumentUploadNavigationEntry, DocumentUploadNavigationFetchContext, onboardingTourRequestAtom, useSetDocumentUploadNavigation } from "recoils";
import { ACTIONS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { MRT_RowSelectionState } from "material-react-table";
import { Box, Divider, Stack } from "@mui/material";
import dayjs from "dayjs";

import { DocumentsQuery } from "api";
import {
  getDateFormatmonthset,
  setToLocalStorage,
  combineDropdownOptions,
  determineAlgorithmName,
  isFeatureEnabled,
  getFromLocalStorage,
  safeParseJSON,
} from "utils";
import {
  useAmplitude,
  useDocuments,
  useGetConfigParameter,
  usePagination,
  useSitesMultiselect,
  useTableFilters,
} from "hooks";
import { LOCAL_STORAGE_KEYS } from "consts";
import {
  ActiveFilters,
  CustomMaterialTable,
  EsgDatePickerField,
  EsgPrimaryLabel,
  InfoMessageBar,
  FLEX_ALIGN_CENTER_GAP_2,
  Loading,
  Switch,
  Pagination,
  useSwitchFilter,
  useYearMonthPeriodFilter,
  BasicButton,
  InfoLabel,
} from "components";
import { OnboardingGuide } from "components/onboarding-guide/OnboardingGuide";
import type { OnboardingGuideRef } from "components/onboarding-guide/types";
import { useTableExpandedRows } from "components/react-table";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import {
  EsgDocumentsUpload,
  EsgDocumentsUploadHandle,
} from "components/documents-upload/esg-flow-document-upload";

import { UTILITY_BILL_PAGE } from "../document-summary-v2/consts";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";
import { StatusBreakdownBar } from "./components/status-breakdown-bar";
import { filterParamsToQuery } from "./helpers/filterParamsToQuery";
import {
  ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES,
  ACTIVE_FLOW_DATA_ENTRIES,
  BASE_DATA_ENTRIES,
  getDataUploadColumns,
  ADD_DOCUMENT_BILL_PAGE,
  ONBOARDING_GUIDE_STEPS,
} from "./consts";
import {
  createDocumentUploadFilterConfig,
  DocumentUploadFilters,
} from "./filter-config";
import { DataEntriesStatusType } from "./types";
import { EsgActions } from "./components/table";

export const DocumentUploadPage = () => {
  // ---- Hooks
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const { data: configParameter } = useGetConfigParameter();
  const { trackEvent } = useAmplitude();

  // ---- Const ----
  const fromDateInitial = dayjs().startOf("month").format("YYYY-MM-DD");
  const toDateInitial = dayjs().add(1, "day").format("YYYY-MM-DD");

  // Local Storage
  const storedFromDate = localStorage.getItem(
    LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE,
  );
  const storedToDate = localStorage.getItem(
    LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE,
  );
  const rawDataEntriesStatus = localStorage.getItem(
    LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER,
  );
  const storedDataEntriesStatus = useMemo(
    () => (rawDataEntriesStatus ? JSON.parse(rawDataEntriesStatus) : []),
    [rawDataEntriesStatus],
  );
  const storedSitesFilter = JSON.parse(
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER,
    ) || "[]",
  );
  const onboardingHidden: boolean =
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_HIDE_ONBOARDING,
    ) === "true";
  const storedSiteIdsFilter = storedSitesFilter.map((site: any) => site.value);
  const viewedDocumentID = getFromLocalStorage("viewedDocumentID");
  const setDocumentUploadNavigation = useSetDocumentUploadNavigation();

  const tourRef = useRef<OnboardingGuideRef>(null);
  const esgDocumentsRef = useRef<EsgDocumentsUploadHandle>(null);
  const uploadRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- States
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [showBulkActions, setShowBulkActions] = useState<boolean>(false);
  const [tourKey, setTourKey] = useState(0);
  const [pendingResumeStep, setPendingResumeStep] = useState<number | null>(
    null,
  );
  const [tourRequested, setTourRequested] = useRecoilState(
    onboardingTourRequestAtom,
  );

  const rowSelectionLength = Object.keys(rowSelection).length > 0;

  const isApprovalFlowValueOn = isFeatureEnabled(
    configParameter,
    "second_layer_approval_feature",
  );
  const isAdditionalStatusesOn = isFeatureEnabled(
    configParameter,
    "unusable_status_enabled",
  );

  // Right below 👇 we have basicStatuses always on, that means = true;
  const dropDownStatusOption: Array<{
    label: string;
    name: string;
    value: number;
  }> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [
      BASE_DATA_ENTRIES,
      ACTIVE_FLOW_DATA_ENTRIES,
      ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES,
    ],
  );

  const [dateError, setDateError] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const initialFromDate = storedFromDate || fromDateInitial;
  const initialToDate = storedToDate || toDateInitial;

  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [dataEntriesStatus, setDataEntriesStatus] = useState<any>([]);

  // ---- API's
  const { setInitialExpanded } = useTableExpandedRows();
  const updateExternalStates = useCallback(() => {
    setInitialExpanded();
  }, [setInitialExpanded]);
  const { availableYears } = useYearMonthPeriodFilter({ updateExternalStates });

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();

  const {
    siteIds,
    isSitesLoading,
    selectedSites,
    setSelectedSites,
    sitesOptions,
  } = useSitesMultiselect({ updateExternalStates });

  const effectiveSiteId = siteIds.length === 1 ? siteIds[0] : undefined;

  const handleSetSites = (param: any) => {
    setSelectedSites(param);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER,
      JSON.stringify(param),
    );
  };

  const handleSetDataEntriesStatus = (param: any) => {
    setDataEntriesStatus(param);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER,
      JSON.stringify(param),
    );
  };

  const { value: isUnmoved, onSwitchChange: onIsUnmovedChange } =
    useSwitchFilter({ key: "isUnmoved", updateExternalStates });

  // searchQuery is state — only updated when Search is clicked
  const [searchQuery, setSearchQuery] = useState({
    uploadDateFrom: initialFromDate,
    uploadDateTo: initialToDate,
    siteIds: storedSiteIdsFilter as number[],
    status: (storedDataEntriesStatus as any[])
      .map((item: any) => item.label)
      .join(","),
    isUnmoved,
  });

  // ---- Pagination (using unified hook)
  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 50,
  });

  // ---- Column filters (same behavior as Site Management: apply/clear workflow, localStorage persistence)
  const filterConfig = useMemo(() => createDocumentUploadFilterConfig(), []);
  const tableFilters = useTableFilters<DocumentUploadFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });
  const mrtFilterProps = tableFilters.getMRTFilterProps();
  const activeFiltersProps = tableFilters.getActiveFiltersProps();

  const onPageChangeHandler = (newPage: number) => {
    onPageChange(newPage);
  };

  const onPageSizeChangeHandler = (newPageSize: number) => {
    onSizeChange(newPageSize);
  };

  const query = useMemo<DocumentsQuery>(
    () => ({
      ...searchQuery,
      ...paginatedQuery,
      ...filterParamsToQuery(
        tableFilters.getQueryParams() as Record<string, any>,
      ),
    }),
    [searchQuery, paginatedQuery, tableFilters],
  );

  const [prevQuery, setPrevQuery] = useState(query);

  // State to track previous dataDocuments for comparison
  const [prevDataDocuments, setPrevDataDocuments] = useState<any>(null);

  const { data: dataDocuments, isLoading, refetch: refetchDocuments } = useDocuments({
    ...query,
    refetchInterval: isPolling ? 30000 : false,
  });

  const handleUploadCompleted = () => {
    setIsPolling(true);
    if (uploadRefetchTimerRef.current) clearTimeout(uploadRefetchTimerRef.current);
    uploadRefetchTimerRef.current = setTimeout(() => {
      refetchDocuments();
      uploadRefetchTimerRef.current = null;
    }, 8000);
  };

  useEffect(() => () => {
    if (uploadRefetchTimerRef.current) clearTimeout(uploadRefetchTimerRef.current);
  }, []);

  const handleSearch = () => {
    setSearchQuery({
      uploadDateFrom: fromDate,
      uploadDateTo: toDate,
      siteIds: siteIds.length > 0 ? siteIds : storedSiteIdsFilter,
      status: dataEntriesStatus.map((item: any) => item.label).join(","),
      isUnmoved,
    });
    tableFilters.applyFilters(tableFilters.pendingFilters);
    onPageChange(1);
  };

  const tableRows = useMemo(
    () => dataDocuments?.documents || [],
    [dataDocuments?.documents],
  );

  const documentNavEntries = useMemo<DocumentUploadNavigationEntry[]>(
    () =>
      tableRows.map((doc: any) => ({
        navId: doc.bills?.length > 0 ? String(doc.bills[0].id) : String(doc.id),
        billIds: (doc.bills ?? []).map((b: any) => String(b.id)),
      })),
    [tableRows],
  );

  const docFetchContext = useMemo<DocumentUploadNavigationFetchContext>(
    () => ({ query, page: pagination.page, size: pagination.size, pageOffset: pagination.page * pagination.size }),
    [query, pagination.page, pagination.size],
  );

  useEffect(() => {
    setDocumentUploadNavigation(null);
  }, [documentNavEntries, setDocumentUploadNavigation]);

  const handleTourStepChange = (stepIndex: number) => {
    if (stepIndex === 0) setIsTourRunning(true);
    if (stepIndex === 22) {
      setShowBulkActions(true);
      if (tableRows.length > 0) setRowSelection({ [tableRows[0].id]: true });
    } else {
      setRowSelection({});
      setShowBulkActions(false);
    }
    if (stepIndex >= 11 && stepIndex <= 21) {
      document.querySelector(`[tour-step="${stepIndex + 1}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  const handleTourStepAfterChange = useCallback(
    (stepIndex: number, action: string) => {
      if (stepIndex === 21 && action === ACTIONS.NEXT) {
        setShowBulkActions(true);
        if (tableRows.length > 0) setRowSelection({ [tableRows[0].id]: true });
        tourRef.current?.stop();
        setPendingResumeStep(22);
      }
    },
    [tableRows],
  );

  const handleTourEnd = () => {
    setIsTourRunning(false);
    setShowBulkActions(false);
    setRowSelection({});
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_HIDE_ONBOARDING,
      "true",
    );
  };

  useEffect(() => {
    if (tourRequested) {
      localStorage.removeItem(
        LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_HIDE_ONBOARDING,
      );
      setTourKey((prev) => prev + 1);
      setTourRequested(false);
    }
  }, [tourRequested, setTourRequested]);

  useEffect(() => {
    if (pendingResumeStep !== null && showBulkActions) {
      document
        .querySelector(".MuiTableContainer-root")
        ?.scrollTo({ left: 0, behavior: "smooth" });
      document
        .querySelector('[tour-step="23"]')
        ?.scrollIntoView({ behavior: "smooth", block: "end" });
      tourRef.current?.resume(pendingResumeStep);
      setPendingResumeStep(null);
    }
  }, [pendingResumeStep, showBulkActions]);

  // Page Handler Functions
  const handleRowSelection = () => {
    setRowSelection({});
  };

  const handleFromDateChange = (date: any) => {
    const day = dayjs(date);
    if (!day.isValid()) {
      return;
    }

    const dateValue = day.startOf("month").format("YYYY-MM-DD");
    const currentToDate =
      typeof toDate === "string" ? toDate : dayjs(toDate).format("YYYY-MM-DD");

    setFromDate(dateValue);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE,
      dateValue,
    );
    setDateError(dateValue > currentToDate);
  };

  const handleToDateChange = (date: any) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const selectedDate = new Date(date);
    if (Number.isNaN(selectedDate.getTime())) {
      return;
    }

    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    let toDateValue: string;

    if (currentMonth === selectedMonth && currentYear === selectedYear) {
      toDateValue = dayjs(today).add(1, "day").format("YYYY-MM-DD");
    } else {
      const lastDateOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      toDateValue = dayjs(lastDateOfMonth).format("YYYY-MM-DD");
    }

    const currentFromDate =
      typeof fromDate === "string"
        ? fromDate
        : dayjs(fromDate).format("YYYY-MM-DD");
    if (toDateValue < currentFromDate) {
      setDateError(true);
      return;
    }

    setToDate(toDateValue);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE,
      toDateValue,
    );
    setDateError(false);
  };

  const handleResetDate = () => {
    setFromDate(fromDateInitial);
    setToDate(toDateInitial);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE,
      fromDateInitial,
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE,
      toDateInitial,
    );
  };

  const handleClearFilters = () => {
    handleResetDate();
    setDateError(false);
    setSelectedSites([]);
    setDataEntriesStatus([]);
    tableFilters.clearFilters();
    onPageChange(1);
    onIsUnmovedChange(false);

    setSearchQuery({
      uploadDateFrom: fromDateInitial,
      uploadDateTo: toDateInitial,
      siteIds: [],
      status: "",
      isUnmoved: false,
    });

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER,
      JSON.stringify([]),
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER,
      JSON.stringify([]),
    );
  };

  const setDateValue = (dateValue: string | null) =>
    dateValue ? dayjs(dateValue) : null;

  const getCircularReplacer = (): any => {
    const seen = new WeakSet();
    return (key: any, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return undefined;
        }
        seen.add(value);
      }
      return value;
    };
  };

  const handleRowClick = (row: any, samePage?: boolean) => {
    const actualRow = samePage ? row?.row : row;
    setToLocalStorage(
      "dataEntries",
      JSON.stringify(actualRow, getCircularReplacer()),
    );
    setToLocalStorage("type", "Upload");
    setToLocalStorage(
      "Upload_URL",
      (currentUrl?.pathname || "") + (currentUrl?.search || ""),
    );
    setToLocalStorage("algorithm", determineAlgorithmName(actualRow.original));
    setToLocalStorage("viewedDocumentID", actualRow.original?.id as string);
    if (actualRow.original?.bills !== null) {
      if (actualRow.original?.bills?.length > 0) {
        setToLocalStorage(
          "summaryDocumentID",
          actualRow.original?.bills[0]?.id,
        );
        if (samePage) {
          setDocumentUploadNavigation({
            entries: documentNavEntries,
            totalCount: dataDocuments?.count ?? documentNavEntries.length,
            fetchContext: docFetchContext,
          });
          const targetPath = actualRow.original.bills.length === 1
            ? UTILITY_BILL_PAGE.ABSOLUTE_PATH(actualRow.original.bills[0].id)
            : UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(actualRow.original.bills[0].id);
          navigate(targetPath);
        }
      } else {
        setToLocalStorage("summaryDocumentID", actualRow.original?.id);
        if (samePage) {
          setDocumentUploadNavigation({
            entries: documentNavEntries,
            totalCount: dataDocuments?.count ?? documentNavEntries.length,
            fetchContext: docFetchContext,
          });
          navigate(
            ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(
              actualRow.original?.id as string,
            ),
          );
        }
      }
    }
  };

  const isProcessingRow = (original: any) =>
    !original?.moved &&
    original?.status !== "PARSED" &&
    original?.status !== "NO_RESPONSE";

  const getMuiTableBodyRowProps = (row: any) => {
    const processing = isProcessingRow(row?.row?.original);
    return {
      onClick: processing ? undefined : () => handleRowClick(row, true),
      ...(row?.row?.index === 1 && { "tour-step": "11" }),
      sx: {
        cursor: processing ? "not-allowed" : "pointer",
        backgroundColor:
          String(row?.row?.original?.id) === String(viewedDocumentID)
            ? "#DCDCDC"
            : "",
      },
    };
  };

  const dataUploadColumns = getDataUploadColumns({ handleRowClick });

  const handleTrackEvents = useCallback(() => {
    // Get new documents by comparing current with previous and filter only new documents (documents that weren't in the previous state)
    const previousDocumentIds =
      prevDataDocuments?.documents?.map((doc: any) => doc.id) || [];
    const newDocuments =
      dataDocuments.documents?.filter(
        (doc: any) => !previousDocumentIds.includes(doc.id),
      ) || [];

    // Track warning events only for new documents that have warnings
    const newDocumentsWithWarnings = newDocuments.filter((doc: any) =>
      doc.dateEntryStatus?.includes("Warning"),
    );
    newDocumentsWithWarnings.forEach(() => {
      trackEvent("AI Processing Warning", "Documents - Upload");
    });

    // Check for new successfully parsed documents (status is PARSED and no warnings) and track success events only for new successfully parsed documents
    const newSuccessfullyParsedDocuments = newDocuments.filter(
      (doc: any) =>
        doc.status === "PARSED" &&
        doc.dateEntryStatus?.includes("Emissions Included"),
    );
    newSuccessfullyParsedDocuments.forEach(() => {
      trackEvent("AI Processing Emissions Included", "Documents - Upload");
    });

    // Check for new signed of documents (status is PARSED and dateEntryStatus includes "Signed Off") and track success events only for new signed of documents
    const newSignedOfDocuments = newDocuments.filter(
      (doc: any) =>
        doc.status === "PARSED" && doc.dateEntryStatus?.includes("Signed Off"),
    );
    newSignedOfDocuments.forEach(() => {
      trackEvent("Approved Signed Off", "Documents - Upload");
    });

    // Check for new locked documents (status is PARSED and dateEntryStatus includes "Locked") and track success events only for new locked documents
    const newLockedDocuments = newDocuments.filter(
      (doc: any) =>
        doc.status === "PARSED" && doc.dateEntryStatus?.includes("Locked"),
    );
    newLockedDocuments.forEach(() => {
      trackEvent("Approved Locked", "Documents - Upload");
    });
  }, [trackEvent, prevDataDocuments, dataDocuments]);

  useEffect(() => {
    localStorage.removeItem("billEntriesIdSelected");

    const currentStoredSitesFilter = safeParseJSON(
      localStorage.getItem(
        LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER,
      ),
      [],
    );
    const currentStoredDataEntriesStatus = safeParseJSON(
      localStorage.getItem(
        LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER,
      ),
      [],
    );

    // Always set from localStorage (even if empty) to ensure URL params are cleared when localStorage is empty
    setSelectedSites(currentStoredSitesFilter ?? []);
    setDataEntriesStatus(currentStoredDataEntriesStatus ?? []);

    if (storedFromDate && storedToDate) {
      const fromDateValue = dayjs(storedFromDate).format("YYYY-MM-DD");
      const toDateValue = dayjs(storedToDate).format("YYYY-MM-DD");
      if (fromDateValue > toDateValue) {
        setDateError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure localStorage takes precedence over URL params (runs after hook reads from URL)
  useEffect(() => {
    if (!isSitesLoading) {
      const currentStoredSitesFilter = safeParseJSON(
        localStorage.getItem(
          LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER,
        ),
        [],
      );
      // If localStorage is empty but sites are selected (from URL), clear them
      if (
        (!currentStoredSitesFilter || currentStoredSitesFilter.length === 0) &&
        selectedSites.length > 0
      ) {
        setSelectedSites([]);
      }
    }
  }, [isSitesLoading, setSelectedSites, selectedSites]);

  useEffect(() => {
    const isDifferent =
      JSON.stringify(searchQuery) !== JSON.stringify(prevQuery);
    if (isDifferent) {
      setPrevQuery(query); // Update previous state if changed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (dataDocuments?.documents && isPolling) {
      const hasProcessing = dataDocuments.documents.some(
        (doc: any) =>
          !doc.moved && doc.status !== "PARSED" && doc.status !== "NO_RESPONSE",
      );
      if (!hasProcessing) setIsPolling(false);
    }
  }, [dataDocuments, isPolling]);

  useEffect(() => {
    if (dataDocuments && dataDocuments?.pageResult) {
      handleTrackEvents();
      setPrevDataDocuments(dataDocuments);
      updateFromApiResponse(dataDocuments.pageResult);
    }
  }, [
    dataDocuments,
    prevDataDocuments,
    updateFromApiResponse,
    handleTrackEvents,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "calc(100dvw - 101px)",
        height: "100%",
        padding: "12px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflowY: "auto",
      }}
    >
      {/* Row 1: Filters */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Box tour-step="3">
            <EsgDatePickerField
              label={
                <InfoLabel
                  title="Start Month"
                  placement="top"
                  tooltipSx={{
                    maxWidth: "180px",
                    height: "fit-content",
                    fontSize: "10px",
                    textAlign: "center",
                  }}
                  labelSx={{
                    width: "100%",
                    height: "18px",
                    padding: "0px",
                    marginBottom: "4px",
                    flexDirection: "row-reverse",
                    justifyContent: "flex-end",
                    backgroundColor: "transparent",
                    "& > svg": {
                      width: "14px",
                      height: "14px",
                      color: "black",
                    },
                  }}
                  titleSx={{
                    color: "black",
                    fontSize: "12px",
                  }}
                  infoMessage="Filter to documents uploaded within the date range starting from this month"
                />
              }
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              format={getDateFormatmonthset()}
              disableFuture
              disableHighlightToday
              onAccept={handleFromDateChange}
              onChange={handleFromDateChange}
              setDate={handleFromDateChange}
              value={setDateValue(fromDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
              width="140px"
            />
          </Box>
          <Box tour-step="4">
            <EsgDatePickerField
              label={
                <InfoLabel
                  title="End Month"
                  placement="top"
                  tooltipSx={{
                    maxWidth: "180px",
                    height: "fit-content",
                    fontSize: "10px",
                    textAlign: "center",
                  }}
                  labelSx={{
                    width: "100%",
                    height: "18px",
                    padding: "0px",
                    marginBottom: "4px",
                    flexDirection: "row-reverse",
                    justifyContent: "flex-end",
                    backgroundColor: "transparent",
                    "& > svg": {
                      width: "14px",
                      height: "14px",
                      color: "black",
                    },
                  }}
                  titleSx={{
                    color: "black",
                    fontSize: "12px",
                  }}
                  infoMessage="Filter to documents uploaded within the date range ending in this month"
                />
              }
              width="140px"
              views={["year", "month"]}
              minDate={minDate}
              openTo="month"
              format={getDateFormatmonthset()}
              disableHighlightToday
              onAccept={handleToDateChange}
              onChange={handleToDateChange}
              setDate={handleToDateChange}
              value={setDateValue(toDate)}
              placeholdervalue="MM-YYYY"
              filterAll={false}
            />
          </Box>
        </Stack>

        <Box
          tour-step="5"
          sx={{ minWidth: "210px", width: "fit-content", paddingLeft: "0px" }}
        >
          <EsgMultiselectControlledField
            label="Sites"
            disabled={isSitesLoading}
            selectedValues={selectedSites}
            setSelectedValues={handleSetSites}
            options={sitesOptions}
            isAllSelectedOnEmptySelection
            type="rounded"
          />
        </Box>

        <Box tour-step="6" sx={{ minWidth: "210px", width: "fit-content" }}>
          <EsgMultiselectControlledField
            label="Data Entries Status"
            disabled={isSitesLoading}
            selectedValues={dataEntriesStatus}
            setSelectedValues={handleSetDataEntriesStatus}
            options={dropDownStatusOption}
            isAllSelectedOnEmptySelection
            type="rounded"
          />
        </Box>

        <Box
          tour-step="7"
          sx={{
            maxWidth: { xl: "125px" },
            "@media (min-width: 1600px)": { maxWidth: "200px" },
          }}
        >
          <Switch
            isChecked={isUnmoved}
            label="Show only unmoved"
            onChange={onIsUnmovedChange}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <BasicButton
            tour-step="8"
            variant="contained"
            sx={{ width: "120px" }}
            disabled={dateError}
            onClick={handleSearch}
          >
            Search
          </BasicButton>
          <BasicButton
            tour-step="9"
            variant="outlined"
            sx={{ width: "120px" }}
            onClick={handleClearFilters}
          >
            Clear Filters
          </BasicButton>
        </Box>
      </Box>

      {dateError && (
        <Box sx={[FLEX_ALIGN_CENTER_GAP_2, { mt: 1 }]}>
          <EsgPrimaryLabel sx={{ color: "red" }}>
            Invalid date selection. Please choose a month after the From Month.
          </EsgPrimaryLabel>
        </Box>
      )}

      {/* Row 2: Divider */}
      <Divider sx={{ borderColor: "#E0E0E0" }} />

      {/* Row 3: Upload button (left) + Bulk actions (right) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={[FLEX_ALIGN_CENTER_GAP_2, { width: "fit-content" }]}
          tour-step="2"
        >
          <EsgDocumentsUpload
            ref={esgDocumentsRef}
            onUploadCompleted={handleUploadCompleted}
            siteId={effectiveSiteId}
          />
        </Box>
        {(rowSelectionLength || (isTourRunning && showBulkActions)) && (
          <Box tour-step="23" sx={{ width: "fit-content", display: "flex" }}>
            <EsgActions
              rowSelection={rowSelection}
              setrowSelection={handleRowSelection}
            />
          </Box>
        )}
      </Box>

      {/* Row 4: StatusBreakdownBar full width */}
      <Box tour-step="10" sx={{ width: "100%" }}>
        <StatusBreakdownBar
          data={dataDocuments as DataEntriesStatusType}
          isApprovalFlowOn={isApprovalFlowValueOn}
          isAdditionalStatusesOn={isAdditionalStatusesOn}
        />
      </Box>

      {isLoading || !dataDocuments ? (
        <Loading />
      ) : (
        <>
          {dataDocuments?.documents.length === 0 && (
            <InfoMessageBar sx={{ marginTop: "20px" }}>
              No bills have been uploaded for this month yet. Please check the
              previous month&apos;s bills or upload more bills.
            </InfoMessageBar>
          )}
          {activeFiltersProps.appliedFilters !== null && (
            <ActiveFilters
              appliedFilters={activeFiltersProps.appliedFilters}
              onClearFilter={activeFiltersProps.onClearFilter}
              displayNameMap={activeFiltersProps.displayNameMap}
              sx={{ marginBottom: 0, width: "100%" }}
            />
          )}
          {tableRows && (
            <>
              <CustomMaterialTable
                enableTopToolbar
                enableStickyHeader
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableRowVirtualization={false}
                enableColumnResizing
                rowClickable
                data={dataDocuments?.documents || []}
                columns={dataUploadColumns}
                enableRowSelection
                enablePagination
                manualPagination
                rowCount={pagination.size}
                onRowSelectionChange={setRowSelection}
                enableColumnFilters={mrtFilterProps.enableColumnFilters}
                manualFiltering={mrtFilterProps.manualFiltering}
                onColumnFiltersChange={mrtFilterProps.onColumnFiltersChange}
                muiTableHeadCellFilterTextFieldProps={
                  mrtFilterProps.muiTableHeadCellFilterTextFieldProps
                }
                initialState={{
                  density: "compact",
                  columnFilters: mrtFilterProps.initialState.columnFilters,
                }}
                state={{ rowSelection, globalFilter: "" }}
                getRowId={(row: any) => row.id}
                containerSx={{
                  "& tbody tr": {
                    "& td:first-of-type": {
                      paddingLeft: "16px",
                    },
                    "& td:nth-of-type(2)": {
                      width: "20px",
                      minWidth: "20px",
                      maxWidth: "20px",
                      paddingLeft: "0px",
                      paddingRight: "25px",
                    },
                  },
                  "& tbody tr:first-of-type td > p": {
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  },
                  height: "calc(100vh - 400px)",
                  minHeight: "200px",
                  minWidth: "100%",
                  maxWidth: "100%",
                  width: "calc(100vw - 150px)",
                }}
                headCellSx={{ lineHeight: "30px" }}
                muiTopToolbarProps={{
                  sx: {
                    backgroundColor: "white",
                    display:
                      Object.keys(rowSelection).length > 0 ? "block" : "none",
                  },
                }}
                muiTableBodyRowProps={(row: any) =>
                  getMuiTableBodyRowProps(row)
                }
                muiSelectCheckboxProps={({ row }) => ({
                  disabled: isProcessingRow(row.original),
                })}
                muiTableBodyCellProps={(row) => ({
                  sx: {
                    opacity: isProcessingRow(row.row.original) ? 0.5 : 1,
                  },
                })}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                <Pagination
                  page={uiPage}
                  rowsCount={pagination.totalRows}
                  rowsPerPage={pagination.size}
                  onPageChange={onPageChangeHandler}
                  onRowsPerPageChange={onPageSizeChangeHandler}
                />
              </Box>
            </>
          )}
          <OnboardingGuide
            ref={tourRef}
            key={tourKey}
            steps={ONBOARDING_GUIDE_STEPS}
            autoStart={!onboardingHidden}
            onStepChange={handleTourStepChange}
            onStepAfterChange={handleTourStepAfterChange}
            onTourEnd={handleTourEnd}
          />
        </>
      )}
    </Box>
  );
};
